import React, { useCallback, useState } from "react";
import { View, ScrollView, Pressable, StyleSheet } from "react-native";
import { useFocusEffect } from "@react-navigation/native";
import { showApiError } from "@components/ds/showApiError";
import { t } from "@constants/localization";
import {
  getDsRoutingSummary,
  getDsReturns,
  getDsRoutingDbList,
} from "@services/api/dsRouting";
import { SCOPES } from "../helpers";
import RoutingHeader from "../components/RoutingHeader";
import { C, RText, ChevronRight } from "../theme";

const PATH = "DsRouting/hub";

// Registered as `DsRoutingHub`; the Routing menu's "Dark Store Routing" item
// opens it (dark store CPs only). Pixel-matched to the mockup's routing hub
// (§10 frame 06): D0 orders (the workspace, red-outlined, red Open), All
// orders (the mockup's overview folder) and Next Orders (read-only).
const DsRoutingHub = ({ navigation }) => {
  const [summary, setSummary] = useState(null);
  const [returns, setReturns] = useState(null);
  const [byDb, setByDb] = useState(null);

  useFocusEffect(
    useCallback(() => {
      let active = true;
      getDsRoutingSummary()
        .then(({ data }) => active && setSummary(data?.data || null))
        .catch((error) => showApiError(error, () => "DsRouting/errorGeneric"));
      // Returned to store: the card shows without a count if this fails.
      getDsReturns()
        .then(({ data }) => active && setReturns(data?.data || null))
        .catch(() => active && setReturns(null));
      // By delivery boy: the card shows without counts if this fails.
      getDsRoutingDbList()
        .then(({ data }) => active && setByDb(data?.data?.dbs || []))
        .catch(() => active && setByDb(null));
      return () => {
        active = false;
      };
    }, []),
  );

  const byDbReady = (byDb || []).reduce(
    (sum, db) => sum + Number(db.ofd_ready || 0),
    0,
  );

  const toRerack = Number(returns?.boxes_to_rerack ?? 0);
  const rerackDbs = (returns?.dbs || []).filter(
    (db) => db.to_rerack > 0,
  ).length;

  const open = (scope) => navigation.navigate("DsRoutingTpList", { scope });

  return (
    <View style={styles.screen}>
      <RoutingHeader title={t(`${PATH}/title`)} navigation={navigation} />
      <ScrollView contentContainerStyle={styles.body}>
        <RText size={13} weight={500} color={C.muted}>
          {t(`${PATH}/introBefore`)}
          <RText size={13} weight={700} color={C.ink}>
            {t(`${PATH}/d0Title`)}
          </RText>
          {t(`${PATH}/introAfter`)}
        </RText>

        <Pressable
          testID="ds-routing-hub-D0"
          onPress={() => open(SCOPES.D0)}
          style={[styles.card, styles.cardPrimary]}
        >
          <View style={styles.info}>
            <RText size={17} weight={700}>
              {t(`${PATH}/d0Title`)}
            </RText>
            <RText size={12.5} weight={500} color={C.muted}>
              {t(`${PATH}/d0Subtitle`)}
            </RText>
            <RText size={13} weight={700} style={styles.count}>
              <RText size={13} weight={700} color={C.red}>
                {t(`${PATH}/toRoute`, {
                  count: String(summary?.d0?.to_route ?? 0),
                })}
              </RText>
              <RText size={13} weight={500} color={C.muted}>
                {t(`${PATH}/routed`, {
                  count: String(summary?.d0?.routed ?? 0),
                })}
              </RText>
            </RText>
          </View>
          <View style={[styles.open, styles.openPrimary]}>
            <RText size={14} weight={600} color="#FFFFFF">
              {t(`${PATH}/open`)}
            </RText>
            <ChevronRight color="#FFFFFF" />
          </View>
        </Pressable>

        <Pressable
          testID="ds-routing-hub-ALL"
          onPress={() => open(SCOPES.ALL)}
          style={styles.card}
        >
          <View style={styles.info}>
            <RText size={17} weight={700}>
              {t(`${PATH}/allTitle`)}
            </RText>
            <RText size={12.5} weight={500} color={C.muted}>
              {t(`${PATH}/allSubtitle`)}
            </RText>
            <RText size={13} weight={700} style={styles.count}>
              {t(`${PATH}/allCount`, {
                count: String(summary?.all?.total ?? 0),
              })}
            </RText>
          </View>
          <View style={[styles.open, styles.openOutline]}>
            <RText size={14} weight={600}>
              {t(`${PATH}/open`)}
            </RText>
            <ChevronRight />
          </View>
        </Pressable>

        <Pressable
          testID="ds-routing-hub-NEXT"
          onPress={() => open(SCOPES.NEXT)}
          style={styles.card}
        >
          <View style={styles.info}>
            <RText size={17} weight={700}>
              {t(`${PATH}/nextTitle`)}
            </RText>
            <RText size={12.5} weight={500} color={C.muted}>
              {t(`${PATH}/nextSubtitle`)}
            </RText>
            <RText size={13} weight={700} style={styles.count}>
              {t(`${PATH}/nextCount`, {
                count: String(summary?.next?.total ?? 0),
              })}
            </RText>
          </View>
          <View style={[styles.open, styles.openOutline]}>
            <RText size={14} weight={600}>
              {t(`${PATH}/open`)}
            </RText>
            <ChevronRight />
          </View>
        </Pressable>

        <Pressable
          testID="ds-routing-hub-BYDB"
          onPress={() => navigation.navigate("DsRoutingDbList")}
          style={styles.card}
        >
          <View style={styles.info}>
            <RText size={17} weight={700}>
              {t(`${PATH}/byDbTitle`)}
            </RText>
            <RText size={12.5} weight={500} color={C.muted}>
              {t(`${PATH}/byDbSubtitle`)}
            </RText>
            {byDb ? (
              <RText size={13} weight={700} style={styles.count}>
                {t(
                  `${PATH}/${byDb.length === 1 ? "byDbCountOne" : "byDbCount"}`,
                  {
                    count: String(byDb.length),
                  },
                )}
                {byDbReady > 0 ? (
                  <RText size={13} weight={700} color={C.green}>
                    {t(`${PATH}/byDbReady`, { count: String(byDbReady) })}
                  </RText>
                ) : null}
              </RText>
            ) : null}
          </View>
          <View style={[styles.open, styles.openOutline]}>
            <RText size={14} weight={600}>
              {t(`${PATH}/open`)}
            </RText>
            <ChevronRight />
          </View>
        </Pressable>

        {/* Always shown, so re-racking opens even at 0 boxes (grey). */}
        <Pressable
          testID="ds-routing-hub-RETURNS"
          onPress={() => navigation.navigate("DsReturnsList")}
          style={[styles.card, toRerack > 0 && styles.cardReturns]}
        >
          <View style={styles.info}>
            <RText size={17} weight={700}>
              {t(`${PATH}/returnsTitle`)}
            </RText>
            <RText size={12.5} weight={500} color={C.muted}>
              {t(`${PATH}/returnsSubtitle`)}
            </RText>
            {returns ? (
              <RText size={13} weight={700} style={styles.count}>
                <RText
                  size={13}
                  weight={700}
                  color={toRerack > 0 ? C.amber : C.muted}
                >
                  {t(
                    `${PATH}/${toRerack === 1 ? "returnsCountOne" : "returnsCount"}`,
                    { count: String(toRerack) },
                  )}
                </RText>
                {rerackDbs > 0 ? (
                  <RText size={13} weight={500} color={C.muted}>
                    {t(
                      `${PATH}/${rerackDbs === 1 ? "returnsDbsOne" : "returnsDbs"}`,
                      { count: String(rerackDbs) },
                    )}
                  </RText>
                ) : null}
              </RText>
            ) : null}
          </View>
          <View style={[styles.open, styles.openOutline]}>
            <RText size={14} weight={600}>
              {t(`${PATH}/open`)}
            </RText>
            <ChevronRight />
          </View>
        </Pressable>
      </ScrollView>
    </View>
  );
};

export default DsRoutingHub;

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: C.page },
  body: { paddingTop: 14, paddingHorizontal: 16, paddingBottom: 24, gap: 12 },
  card: {
    backgroundColor: C.surface,
    borderWidth: 1,
    borderColor: C.border,
    borderRadius: 14,
    padding: 16,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  cardPrimary: { borderWidth: 2, borderColor: C.red },
  cardReturns: { borderColor: C.amberBg, borderWidth: 1.5 },
  info: { flex: 1, gap: 4 },
  count: { paddingTop: 3 },
  open: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 9,
    flexShrink: 0,
  },
  openPrimary: { backgroundColor: C.red },
  openOutline: {
    backgroundColor: C.surface,
    borderWidth: 1.5,
    borderColor: C.border,
  },
});
